// helpers/handleDrawerClose.js
import PropTypes from 'prop-types'
import { Logger } from '../logger'
import isEqual from 'lodash/isEqual'
/**
 * Generic close handler for item drawers (calibrations, services, parts, labour).
 * Works for both workorders and quotes by accepting `type` and `sourceData`.
 *
 * @param {Object} params - The parameters.
 * @param {Function} [params.setLoading] - Optional setter for loading state.
 * @param {Function} params.setDrawerOpen - Setter to close the drawer.
 * @param {Function} params.updateFn - API function to update item list.
 * @param {Function} params.setItems - Setter for local items state.
 * @param {Object} params.sourceData - The quote or work order object.
 * @param {Array} params.data - The updated items to send.
 * @param {String} params.logFile - The file name for logging.
 * @param {String} params.itemKey - The API payload key (e.g., 'calibrations', 'services').
 * @param {String} params.type - Either 'workorders' or 'quotes'.
 */

const extractItemNums = (items = []) => items.map((item) => item.name).sort()

export const handleDrawerClose = async ({
  setLoading = () => {},
  setDrawerOpen,
  updateFn,
  setItems,
  sourceData,
  data,
  logFile,
  itemKey,
  type,
  onSuccess
}) => {
  try {
    const original = sourceData?.[itemKey] || []
    const originalItemNums = extractItemNums(original)
    const newItemNums = extractItemNums(data)
    const isSame = isEqual(originalItemNums, newItemNums)

    setDrawerOpen(false)

    if (isSame) return

    setLoading(true)

    const payload = {
      type,
      id: sourceData.id,
      [itemKey]: data
    }

    const response = await updateFn(payload)
    if (response?.status === 200) {
      setItems(data)
      if (onSuccess) onSuccess()
    }
  } catch (error) {
    Logger.error({
      message: error,
      payload: {
        file: `${type === 'quotes' ? 'QuoteSummaryPage' : 'WorkOrderSummaryPage'}/${logFile}`,
        id: sourceData.id
      }
    })
  } finally {
    setLoading(false)
  }
}

handleDrawerClose.propTypes = {
  setLoading: PropTypes.func,
  setDrawerOpen: PropTypes.func.isRequired,
  updateFn: PropTypes.func.isRequired,
  setItems: PropTypes.func.isRequired,
  sourceData: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  logFile: PropTypes.string.isRequired,
  itemKey: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['workorders', 'quotes']).isRequired
}
